import { useAsync } from "./async.js";
import { useDevices, usePlatform } from "./context.js";
import { DeviceType } from "@variocube/app-api";
import { useCallback, useEffect, useMemo, useState } from "react";
export var PaymentError;
(function (PaymentError) {
    PaymentError["Denied"] = "denied";
    PaymentError["Confirmation"] = "confirmation";
    PaymentError["Vending"] = "vending";
    PaymentError["System"] = "system";
})(PaymentError || (PaymentError = {}));
export function usePayment(options) {
    const { amount, currency, language, onInitiate, onVend, onConfirmed, onConfirmationFailed, } = options;
    const [message, setMessage] = useState([]);
    const platform = usePlatform();
    const { loading, execute, error } = useAsync(async () => {
        const { paymentTerminal } = platform;
        // call initiate handler
        try {
            onInitiate && await onInitiate();
        }
        catch (error) {
            console.error("error in initiate handler", error);
            throw PaymentError.System;
        }
        // initiate payment
        let payment;
        try {
            payment = await paymentTerminal.initiatePayment(amount, currency, language, setMessage);
        }
        catch (error) {
            if (error === null || error === void 0 ? void 0 : error.systemError) {
                console.error("payment system error", error);
                throw PaymentError.System;
            }
            else {
                // payment denied, allow to try another card
                console.error("payment denied. try again", error);
                throw PaymentError.Denied;
            }
        }
        try {
            // call handler for approved payment
            await onVend(payment);
        }
        catch (error) {
            console.error("error in payment approved handler", error);
            // vending failed, cancel payment
            await paymentTerminal.cancelPayment();
            throw PaymentError.Vending;
        }
        try {
            // confirm payment
            await paymentTerminal.confirmPayment();
            // notify that payment was confirmed
            onConfirmed && await onConfirmed();
        }
        catch (error) {
            console.error("error confirming payment", error);
            // notify that payment confirmation failed
            if (onConfirmationFailed) {
                await onConfirmationFailed();
            }
            throw PaymentError.Confirmation;
        }
    }, [platform, amount, currency, onVend, onConfirmed, onConfirmationFailed]);
    const retry = useCallback(async () => {
        if (!loading) {
            await execute();
        }
    }, [loading, execute]);
    const cancel = useCallback(async () => {
        await platform.paymentTerminal.cancelPayment();
    }, [platform]);
    /**
     * Wallee Payment Terminal does not support cancel via API.
     * User should cancel on the payment terminal display instead.
     */
    const devices = useDevices();
    const isWalleeTerminal = useMemo(() => devices
        .find(device => device.types.includes(DeviceType.PaymentTerminal)
        && device.model === 'Wallee') === undefined, [devices]);
    const canCancel = (!isWalleeTerminal && loading) || error == PaymentError.System || error == PaymentError.Denied;
    // cancel any payment on unmount
    useEffect(() => {
        return () => {
            if (canCancel) {
                cancel();
            }
        };
    }, [canCancel, cancel]);
    return useMemo(() => ({
        processing: loading,
        message,
        error: error,
        cancel,
        canCancel,
        retry,
        canRetry: error == PaymentError.Denied,
    }), [loading, message, error, cancel, retry]);
}
//# sourceMappingURL=payment.js.map